// src/Participants.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  TableContainer,
  Chip,
  Divider,
  Tooltip,
  InputAdornment,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { styled } from '@mui/system';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import SearchIcon from '@mui/icons-material/Search';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BadgeIcon from '@mui/icons-material/Badge';
import { serverTimestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { getAllItems, addItem, updateItem } from './firestore';

const ModernButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.10)',
  },
});

const PrimaryButton = styled(ModernButton)({
  backgroundColor: '#000',
  color: '#fff',
  '&:hover': { backgroundColor: '#222' },
  '&:disabled': { backgroundColor: '#111', color: 'rgba(255,255,255,0.6)' },
});

const SubtleButton = styled(ModernButton)({
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0,0,0,0.15)',
  '&:hover': { backgroundColor: '#fafafa' },
});

const StatPill = ({ icon, label, value }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.5,
      py: 1,
      borderRadius: 999,
      border: '1px solid rgba(0,0,0,0.08)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(6px)',
    }}
  >
    <Box sx={{ display: 'inline-flex', alignItems: 'center', opacity: 0.85 }}>{icon}</Box>
    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)' }}>
      {label}
    </Typography>
    <Chip
      size="small"
      label={value}
      sx={{
        ml: 0.5,
        fontWeight: 800,
        bgcolor: 'rgba(0,0,0,0.06)',
        borderRadius: 999,
      }}
    />
  </Paper>
);

const Participants = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [participants, setParticipants] = useState([]);
  const [name, setName] = useState('');
  const [ndisNumber, setNdisNumber] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  // premium UX state
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState('name-asc'); // name-asc | name-desc | newest
  const [showArchived, setShowArchived] = useState(false);

  // snackbars instead of alerts
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadParticipants = async () => {
      try {
        const loaded = await getAllItems(currentUser.uid, 'participants');
        setParticipants(loaded || []);
      } catch (error) {
        console.error('Error loading participants:', error);
        showToast('error', 'Failed to load participants.');
      } finally {
        setLoading(false);
      }
    };

    loadParticipants();
  }, [currentUser, navigate]);

  const resetForm = () => {
    setName('');
    setNdisNumber('');
    setEditingId(null);
  };

  const canSubmit = name.trim().length > 0 && /^\d{9}$/.test(ndisNumber.trim());

  const filteredParticipants = useMemo(() => {
    const q = query.trim().toLowerCase();

    const base = (participants || []).filter((p) => {
      if (!showArchived && p.archived) return false;
      if (!q) return true;
      const n = (p.name || '').toLowerCase();
      const num = (p.ndisNumber || '').toLowerCase();
      return n.includes(q) || num.includes(q);
    });

    const getCreatedMs = (p) => {
      // handles Firestore Timestamp, {seconds}, or missing
      const ts = p?.createdAt;
      if (!ts) return 0;
      if (typeof ts.toDate === 'function') return ts.toDate().getTime();
      if (typeof ts.seconds === 'number') return ts.seconds * 1000;
      return 0;
    };

    const sorted = [...base].sort((a, b) => {
      if (sortBy === 'name-desc') return (b.name || '').localeCompare(a.name || '');
      if (sortBy === 'newest') return getCreatedMs(b) - getCreatedMs(a);
      return (a.name || '').localeCompare(b.name || '');
    });

    return sorted;
  }, [participants, query, showArchived, sortBy]);

  const activeCount = useMemo(
    () => (participants || []).filter((p) => !p.archived).length,
    [participants]
  );
  const archivedCount = useMemo(
    () => (participants || []).filter((p) => p.archived).length,
    [participants]
  );

  const handleSave = async () => {
    const cleanName = name.trim();
    const cleanNdis = ndisNumber.trim();

    if (!cleanName || !cleanNdis) return showToast('warning', 'Please fill in all fields.');
    if (!/^\d{9}$/.test(cleanNdis)) return showToast('warning', 'NDIS Number must be exactly 9 digits.');

    const duplicate = participants.find((p) => p.ndisNumber === cleanNdis && p.id !== editingId);
    if (duplicate) return showToast('error', 'That NDIS number is already saved.');

    const participantData = {
      name: cleanName,
      ndisNumber: cleanNdis,
      archived: false,

      // Profile defaults
      planStart: '',
      planEnd: '',
      goals: [],
      addressLine1: '',
      addressLine2: '',
      suburb: '',
      state: '',
      postcode: '',
      billingPreference: 'plan-managed', // 'ndia' | 'plan-managed' | 'self-managed'

      contacts: {
        planManagerName: '',
        planManagerEmail: '',
        planManagerPhone: '',
        supportCoordinatorName: '',
        supportCoordinatorEmail: '',
        supportCoordinatorPhone: '',
        guardianName: '',
        guardianEmail: '',
        guardianPhone: '',
        emergencyContacts: [],
      },

      updatedAt: serverTimestamp(),
      ...(editingId ? {} : { createdAt: serverTimestamp() }),
    };

    try {
      if (editingId) {
        const existing = participants.find((p) => p.id === editingId) || {};
        const merged = {
          ...participantData,
          ...existing,
          name: participantData.name,
          ndisNumber: participantData.ndisNumber,
          updatedAt: serverTimestamp(),
        };

        await updateItem(currentUser.uid, 'participants', editingId, merged);
        setParticipants((prev) => prev.map((p) => (p.id === editingId ? { ...p, ...merged } : p)));
        showToast('success', 'Participant updated.');
      } else {
        const newParticipant = await addItem(currentUser.uid, 'participants', participantData);
        setParticipants((prev) => [...prev, newParticipant]);
        showToast('success', 'Participant added.');
      }

      resetForm();
    } catch (error) {
      console.error('Save failed:', error);
      showToast('error', `Failed to save participant: ${error.message}`);
    }
  };

  const handleEdit = (participant) => {
    setName(participant.name || '');
    setNdisNumber(participant.ndisNumber || '');
    setEditingId(participant.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleArchive = async (id) => {
    const participant = participants.find((p) => p.id === id);
    const label = participant?.name ? `"${participant.name}"` : 'this participant';

    if (
      !window.confirm(
        `Archive ${label}?\n\nThis keeps invoices and reports intact, but hides them from dropdowns.`
      )
    ) {
      return;
    }

    try {
      await updateItem(currentUser.uid, 'participants', id, {
        archived: true,
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setParticipants((prev) => prev.map((p) => (p.id === id ? { ...p, archived: true } : p)));
      if (editingId === id) resetForm();
      showToast('success', 'Participant archived.');
    } catch (error) {
      console.error('Archive failed:', error);
      showToast('error', `Failed to archive participant: ${error.message}`);
    }
  };

  const billingLabelFor = (p) => {
    const billing = p.billingPreference || 'plan-managed';
    return billing === 'ndia' ? 'NDIA' : billing === 'self-managed' ? 'Self-managed' : 'Plan-managed';
  };

  const billingChipSx = (p) => {
    const billing = p.billingPreference || 'plan-managed';
    const map = {
      ndia: { bgcolor: 'rgba(25,118,210,0.10)', color: 'rgb(25,118,210)' },
      'self-managed': { bgcolor: 'rgba(46,125,50,0.10)', color: 'rgb(46,125,50)' },
      'plan-managed': { bgcolor: 'rgba(0,0,0,0.06)', color: 'rgba(0,0,0,0.75)' },
    };
    const style = map[billing] || map['plan-managed'];
    return { borderRadius: 999, fontWeight: 700, ...style };
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading participants...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Page header */}
      <Box
        sx={{
          mb: 2.5,
          p: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(245,247,255,1) 45%, rgba(245,255,248,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SubtleButton onClick={() => navigate('/')}>Back</SubtleButton>

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography
              variant="h4"
              sx={{
                color: '#000',
                fontWeight: 900,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              Participants
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mt: 0.5 }}>
              Add and manage participant profiles. Click a row to open the full profile.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <StatPill icon={<PeopleAltIcon fontSize="small" />} label="Active" value={activeCount} />
            <StatPill icon={<BadgeIcon fontSize="small" />} label="Archived" value={archivedCount} />
          </Stack>
        </Box>
      </Box>

      {/* Add / Edit Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'white',
          boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 999,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(0,0,0,0.06)',
            }}
          >
            <PersonAddAlt1Icon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 200 }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {editingId ? 'Edit Participant' : 'Add Participant'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
              NDIS number must be exactly 9 digits.
            </Typography>
          </Box>

          <Box sx={{ flex: 1 }} />

          <Chip
            size="small"
            label="Pro tip: Open profile from the table"
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              bgcolor: 'rgba(0,0,0,0.06)',
              fontWeight: 700,
              borderRadius: 999,
            }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            label="Participant Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ width: { xs: '100%', sm: 360 } }}
          />
          <TextField
            label="NDIS Number (9 digits)"
            value={ndisNumber}
            onChange={(e) => setNdisNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={{ width: { xs: '100%', sm: 240 } }}
          />

          <PrimaryButton variant="contained" onClick={handleSave} disabled={!canSubmit}>
            {editingId ? 'Save Changes' : 'Add Participant'}
          </PrimaryButton>

          {editingId && (
            <SubtleButton variant="outlined" onClick={resetForm}>
              Cancel
            </SubtleButton>
          )}
        </Box>
      </Paper>

      {/* Controls */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'white',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or NDIS number…"
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            size="medium"
            sx={{ minWidth: 180, borderRadius: 2 }}
          >
            <MenuItem value="name-asc">Sort: Name (A–Z)</MenuItem>
            <MenuItem value="name-desc">Sort: Name (Z–A)</MenuItem>
            <MenuItem value="newest">Sort: Newest</MenuItem>
          </Select>

          <FormControlLabel
            control={<Switch checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
            label="Show archived"
            sx={{ ml: 0.5 }}
          />
        </Box>
      </Paper>

      {/* List */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: 'white',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: 'rgba(0,0,0,0.03)',
                  '& th': {
                    fontWeight: 900,
                    color: 'rgba(0,0,0,0.75)',
                    py: 1.5,
                  },
                }}
              >
                <TableCell>Name</TableCell>
                <TableCell>NDIS Number</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredParticipants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography sx={{ fontWeight: 900, mb: 0.5 }}>No matches</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                      Try changing your search, or add a new participant above.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredParticipants.map((p, idx) => (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      bgcolor: idx % 2 === 0 ? 'rgba(0,0,0,0.00)' : 'rgba(0,0,0,0.01)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.03)' },
                    }}
                    onClick={() => navigate(`/participants/${p.id}`)}
                  >
                    <TableCell sx={{ fontWeight: 800 }}>{p.name}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{p.ndisNumber}</TableCell>
                    <TableCell>
                      <Chip size="small" label={billingLabelFor(p)} sx={billingChipSx(p)} />
                    </TableCell>
                    <TableCell>
                      {p.archived ? (
                        <Chip
                          size="small"
                          label="Archived"
                          sx={{
                            borderRadius: 999,
                            bgcolor: 'rgba(211,47,47,0.10)',
                            color: 'rgb(211,47,47)',
                            fontWeight: 800,
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Active"
                          sx={{
                            borderRadius: 999,
                            bgcolor: 'rgba(46,125,50,0.10)',
                            color: 'rgb(46,125,50)',
                            fontWeight: 800,
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell
                      align="center"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      <Tooltip title="Open Profile">
                        <IconButton size="small" onClick={() => navigate(`/participants/${p.id}`)}>
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit name / NDIS">
                        <IconButton size="small" onClick={() => handleEdit(p)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Archive">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleArchive(p.id)}
                            color="error"
                            disabled={!!p.archived}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Participants;





